import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { CPStoreSyncGateway } from '../gateways/CPStoreSyncGateway'
import { STORE_SYNC_FAKE_PROGRESS, STORE_SYNC_STATUS_TTL_MS, STORE_SYNC_SUCCESS_BANNER_MS, STORE_SYNC_TIMEOUT_MS } from '../constants/store-sync-timing'
import {
  STORE_SYNC_PARTS,
  STORE_SYNC_TIMEOUT_ERROR,
  type StoreSyncModalPhase,
  type StoreSyncPart,
  type StoreSyncStatus,
  type StoreSyncStepState
} from '../models/StoreSyncStatus'

export type StoreSyncBannerPhase = 'success' | 'error'

export interface StoreSyncFetchStatusOptions {
  force?: boolean
}

const useStoreSyncStore = defineStore('store/sync', () => {
  const status = ref<StoreSyncStatus | null>(null)
  const currentAppId = ref<number | null>(null)
  const statusFetchedAt = ref<number | null>(null)
  let fetchStatusPromise: Promise<void> | null = null
  let fetchStatusAppId: number | null = null
  let fetchStatusGeneration = 0
  const modalOpen = ref(false)
  const modalPhase = ref<StoreSyncModalPhase>('review')
  const selectedParts = ref<StoreSyncPart[]>([...STORE_SYNC_PARTS])
  const activeParts = ref<StoreSyncPart[]>([])
  const optimisticPendingParts = ref<Set<StoreSyncPart>>(new Set())
  const fakeProgressBoost = ref(0)
  const lastRealProgressAt = ref(0)
  const syncStartedAt = ref<number | null>(null)
  const bannerPhase = ref<StoreSyncBannerPhase | null>(null)
  let successBannerTimeoutId: ReturnType<typeof setTimeout> | null = null

  const syncing = computed(() => status.value?.syncing ?? false)
  const needsSync = computed(() =>
    optimisticPendingParts.value.size > 0 || (status.value?.needsSync ?? false)
  )

  const isMenuPending = computed(() =>
    optimisticPendingParts.value.has('menu') || (status.value?.parts.menu.needsSync ?? false)
  )

  const pendingSyncParts = computed((): StoreSyncPart[] =>
    STORE_SYNC_PARTS.filter(part =>
      optimisticPendingParts.value.has(part) || (status.value?.parts[part].needsSync ?? false)
    )
  )

  const isMenuOnlyPending = computed(() =>
    pendingSyncParts.value.length === 1 && pendingSyncParts.value[0] === 'menu'
  )

  const inferSyncStartedAt = (result: StoreSyncStatus): number | null => {
    const candidateParts = result.activeBatch?.length
      ? resolveBatchParts(result)
      : STORE_SYNC_PARTS.filter(part => result.parts[part].isSyncing)

    const timestamps = candidateParts
      .map(part => result.parts[part].syncRequestedAt)
      .filter((value): value is string => value !== null && value !== '')
      .map(value => Date.parse(value))
      .filter(value => !Number.isNaN(value))

    if (timestamps.length === 0) {
      return null
    }

    return Math.min(...timestamps)
  }

  const updateSyncStartedAt = (result: StoreSyncStatus): void => {
    if (!result.syncing) {
      syncStartedAt.value = null
      return
    }

    const inferred = inferSyncStartedAt(result)
    if (inferred !== null) {
      syncStartedAt.value = inferred
      return
    }

    if (syncStartedAt.value === null) {
      syncStartedAt.value = Date.now()
    }
  }

  const getSyncElapsedMs = (): number => {
    if (syncStartedAt.value === null) {
      return 0
    }

    return Date.now() - syncStartedAt.value
  }

  const hasSyncTimeoutError = (result: StoreSyncStatus): boolean =>
    STORE_SYNC_PARTS.some(part => result.parts[part].errorMessage === STORE_SYNC_TIMEOUT_ERROR)

  const hasSyncError = (result: StoreSyncStatus): boolean =>
    hasSyncTimeoutError(result) ||
    STORE_SYNC_PARTS.some(part => result.parts[part].errorMessage)

  const clearSuccessBannerTimer = (): void => {
    if (successBannerTimeoutId) {
      clearTimeout(successBannerTimeoutId)
      successBannerTimeoutId = null
    }
  }

  const dismissBanner = (): void => {
    clearSuccessBannerTimer()
    bannerPhase.value = null
  }

  const showSuccessBanner = (): void => {
    clearSuccessBannerTimer()
    bannerPhase.value = 'success'
    successBannerTimeoutId = setTimeout(() => {
      if (bannerPhase.value === 'success') {
        bannerPhase.value = null
      }
      successBannerTimeoutId = null
    }, STORE_SYNC_SUCCESS_BANNER_MS)
  }

  const showErrorBanner = (): void => {
    clearSuccessBannerTimer()
    bannerPhase.value = 'error'
  }

  const handleSyncCompletionBanner = (result: StoreSyncStatus, batchParts: StoreSyncPart[]): void => {
    if (hasSyncError(result)) {
      if (activeParts.value.length === 0 && batchParts.length > 0) {
        activeParts.value = batchParts.filter(part => result.parts[part].errorMessage)
      }
      showErrorBanner()
      return
    }

    if (!result.needsSync) {
      showSuccessBanner()
    }
  }

  const resolveErrorBatchParts = (result: StoreSyncStatus): StoreSyncPart[] => {
    if (activeParts.value.length > 0) {
      return activeParts.value
    }

    if (result.activeBatch?.length) {
      return resolveBatchParts(result)
    }

    return STORE_SYNC_PARTS.filter(part => result.parts[part].errorMessage)
  }

  const resolveBatchParts = (result: StoreSyncStatus): StoreSyncPart[] => {
    if (result.activeBatch?.length) {
      return STORE_SYNC_PARTS.filter(part => result.activeBatch!.includes(part))
    }

    return STORE_SYNC_PARTS.filter(part => result.parts[part].isSyncing)
  }

  const displayParts = computed((): StoreSyncPart[] => {
    if (activeParts.value.length > 0) {
      return activeParts.value
    }
    if (modalPhase.value === 'error' && status.value) {
      return resolveErrorBatchParts(status.value)
    }
    if (!status.value?.syncing) {
      return []
    }
    return resolveBatchParts(status.value)
  })

  const syncActivePartsFromStatus = (result: StoreSyncStatus): void => {
    if (!result.syncing) {
      return
    }

    if (result.activeBatch?.length) {
      activeParts.value = resolveBatchParts(result)
      return
    }

    if (activeParts.value.length > 0) {
      return
    }

    const syncingParts = resolveBatchParts(result)
    if (syncingParts.length > 0) {
      activeParts.value = syncingParts
    }
  }

  const computeActualProgressPercent = (): number => {
    const parts = displayParts.value
    if (parts.length === 0) { return 0 }
    if (modalPhase.value === 'success') { return 100 }

    let score = 0
    for (const part of parts) {
      const partStatus = status.value?.parts[part]
      if (!partStatus || partStatus.errorMessage) { continue }
      if (!partStatus.isSyncing && !partStatus.needsSync) {
        score += 1
      }
    }

    return Math.round((score / parts.length) * 100)
  }

  const computeFakeProgressCap = (): number => {
    const parts = displayParts.value
    if (parts.length === 0) {
      return STORE_SYNC_FAKE_PROGRESS.maxPercent
    }

    let completedCount = 0
    let hasInProgress = false

    for (const part of parts) {
      const partStatus = status.value?.parts[part]
      if (!partStatus || partStatus.errorMessage) { continue }

      if (!partStatus.isSyncing && !partStatus.needsSync) {
        completedCount += 1
      } else if (partStatus.isSyncing) {
        hasInProgress = true
      }
    }

    const partShare = 100 / parts.length
    const completedPercent = completedCount * partShare
    const inProgressPercent = hasInProgress
      ? partShare * STORE_SYNC_FAKE_PROGRESS.inProgressFillRatio
      : 0

    return Math.min(
      STORE_SYNC_FAKE_PROGRESS.maxPercent,
      Math.round(completedPercent + inProgressPercent)
    )
  }

  const markRealProgressNow = (): void => {
    lastRealProgressAt.value = Date.now()
  }

  const resetFakeProgress = (): void => {
    fakeProgressBoost.value = 0
    markRealProgressNow()
  }

  const beginFakeProgress = (): void => {
    fakeProgressBoost.value = STORE_SYNC_FAKE_PROGRESS.initialPercent
    markRealProgressNow()
  }

  const tickFakeProgress = (): void => {
    const maxFake = computeFakeProgressCap()

    if (fakeProgressBoost.value >= maxFake) { return }

    const remaining = maxFake - fakeProgressBoost.value
    const increment = Math.max(
      STORE_SYNC_FAKE_PROGRESS.minIncrementPerTick,
      remaining * STORE_SYNC_FAKE_PROGRESS.incrementFactor
    )

    fakeProgressBoost.value = Math.min(maxFake, fakeProgressBoost.value + increment)
  }

  const noteActualProgressChange = (nextActual: number, previousActual: number): void => {
    if (nextActual > previousActual) {
      resetFakeProgress()
    }
  }

  const pruneOptimisticPendingParts = (result: StoreSyncStatus): void => {
    if (optimisticPendingParts.value.size === 0) {
      return
    }

    const next = new Set(optimisticPendingParts.value)
    STORE_SYNC_PARTS.forEach((part) => {
      if (!result.parts[part].needsSync) {
        next.delete(part)
      }
    })
    optimisticPendingParts.value = next
  }

  const applyStatus = (result: StoreSyncStatus, previousStatus: StoreSyncStatus | null = status.value): void => {
    const wasSyncing = previousStatus?.syncing ?? false
    const batchPartsSnapshot = activeParts.value.length > 0
      ? [...activeParts.value]
      : resolveBatchParts(result)
    const previousActual = computeActualProgressPercent()
    status.value = result
    updateSyncStartedAt(result)
    syncActivePartsFromStatus(result)
    noteActualProgressChange(computeActualProgressPercent(), previousActual)

    if (result.syncing) {
      dismissBanner()
    } else if (wasSyncing) {
      if (modalPhase.value === 'syncing') {
        const batchParts = resolveErrorBatchParts(result)
        const hasError = batchParts.some(part => result.parts[part].errorMessage) ||
          hasSyncTimeoutError(result)
        modalPhase.value = hasError ? 'error' : 'success'
        if (hasError && activeParts.value.length === 0) {
          activeParts.value = batchParts
        }
      }
      if (modalPhase.value !== 'error') {
        activeParts.value = []
      }
      resetFakeProgress()
      handleSyncCompletionBanner(result, batchPartsSnapshot)
    } else if (hasSyncError(result)) {
      showErrorBanner()
    }

    pruneOptimisticPendingParts(result)
    statusFetchedAt.value = Date.now()
  }

  const isStatusFresh = (appId: number): boolean =>
    status.value !== null &&
    currentAppId.value === appId &&
    !syncing.value &&
    statusFetchedAt.value !== null &&
    Date.now() - statusFetchedAt.value < STORE_SYNC_STATUS_TTL_MS

  const fetchStatus = (appId: number, options: StoreSyncFetchStatusOptions = {}): Promise<void> => {
    const { force = false } = options
    currentAppId.value = appId

    if (!force && isStatusFresh(appId)) {
      return Promise.resolve()
    }

    if (!force && fetchStatusPromise && fetchStatusAppId === appId) {
      return fetchStatusPromise
    }

    const generation = ++fetchStatusGeneration
    const promise = (async (): Promise<void> => {
      try {
        const result = await CPStoreSyncGateway.instance().getStatus(appId)
        if (result) {
          applyStatus(result)
        }
      } catch {
        // non-critical
      } finally {
        if (fetchStatusAppId === appId && fetchStatusGeneration === generation) {
          fetchStatusPromise = null
          fetchStatusAppId = null
        }
      }
    })()

    if (!force) {
      fetchStatusAppId = appId
      fetchStatusPromise = promise
    }

    return promise
  }

  const refreshIfSyncing = async (): Promise<void> => {
    if (!currentAppId.value || !syncing.value) { return }
    await fetchStatus(currentAppId.value, { force: true })
  }

  const openReviewModal = (parts?: StoreSyncPart[]): void => {
    if (parts?.length) {
      selectedParts.value = [...parts]
    } else {
      selectedParts.value = STORE_SYNC_PARTS.filter(part =>
        optimisticPendingParts.value.has(part) || status.value?.parts[part].needsSync
      )
      if (selectedParts.value.length === 0) {
        selectedParts.value = [...STORE_SYNC_PARTS]
      }
    }
    modalPhase.value = 'review'
    modalOpen.value = true
  }

  const openProgressModal = (): void => {
    if (status.value?.syncing) {
      syncActivePartsFromStatus(status.value)
      modalPhase.value = 'syncing'
    } else if (status.value) {
      const errorParts = resolveErrorBatchParts(status.value)
      const hasError = errorParts.some(part => status.value!.parts[part].errorMessage)
      if (hasError) {
        if (activeParts.value.length === 0) {
          activeParts.value = errorParts
        }
        modalPhase.value = 'error'
      } else {
        modalPhase.value = 'review'
      }
    }
    modalOpen.value = true
  }

  const openErrorModal = (): void => {
    if (status.value && activeParts.value.length === 0) {
      const errorParts = resolveErrorBatchParts(status.value)
      if (errorParts.length > 0) {
        activeParts.value = errorParts
      }
    }
    openProgressModal()
  }

  const closeModal = (): void => {
    modalOpen.value = false
  }

  const triggerSync = async (appId: number, parts: StoreSyncPart[]): Promise<void> => {
    if (parts.length === 0) { return }

    currentAppId.value = appId

    try {
      const result = await CPStoreSyncGateway.instance().sync(appId, parts)
      if (result) {
        applyStatus(result)
      }
    } catch {
      await fetchStatus(appId, { force: true })
    }
  }

  const syncSelected = async (appId: number): Promise<boolean> => {
    currentAppId.value = appId
    activeParts.value = [...selectedParts.value]
    const previousStatus = status.value
    status.value = null
    modalPhase.value = 'syncing'
    modalOpen.value = true
    syncStartedAt.value = Date.now()
    dismissBanner()
    resetFakeProgress()

    try {
      const result = await CPStoreSyncGateway.instance().sync(appId, selectedParts.value)
      if (result) {
        applyStatus(result, previousStatus)
        return true
      }
      modalPhase.value = 'error'
      showErrorBanner()
      return false
    } catch {
      modalPhase.value = 'error'
      showErrorBanner()
      if (currentAppId.value) {
        await fetchStatus(currentAppId.value, { force: true })
      }
      return false
    }
  }

  const syncAll = (appId: number): Promise<boolean> => {
    selectedParts.value = [...STORE_SYNC_PARTS]
    return syncSelected(appId)
  }

  const retrySync = (appId: number): Promise<boolean> => {
    selectedParts.value = activeParts.value.length > 0
      ? [...activeParts.value]
      : [...selectedParts.value]
    return syncSelected(appId)
  }

  const handleSyncTimeout = async (): Promise<void> => {
    if (!currentAppId.value || !syncing.value) {
      return
    }

    if (getSyncElapsedMs() < STORE_SYNC_TIMEOUT_MS) {
      return
    }

    await fetchStatus(currentAppId.value, { force: true })
  }

  const markPartStale = (part: StoreSyncPart): void => {
    optimisticPendingParts.value = new Set([...optimisticPendingParts.value, part])
  }

  const toggleSelectedPart = (part: StoreSyncPart): void => {
    if (selectedParts.value.includes(part)) {
      selectedParts.value = selectedParts.value.filter(p => p !== part)
    } else {
      selectedParts.value = [...selectedParts.value, part]
    }
  }

  const selectAllParts = (): void => {
    selectedParts.value = [...STORE_SYNC_PARTS]
  }

  const clearSelectedParts = (): void => {
    selectedParts.value = []
  }

  const setSelectAll = (selected: boolean): void => {
    selectedParts.value = selected ? [...STORE_SYNC_PARTS] : []
  }

  const actualProgressPercent = computed((): number => computeActualProgressPercent())

  const displayProgressPercent = computed((): number => {
    if (modalPhase.value === 'success') { return 100 }
    if (!syncing.value) { return actualProgressPercent.value }

    const actual = actualProgressPercent.value
    const boosted = Math.max(actual, Math.round(fakeProgressBoost.value))

    return Math.min(computeFakeProgressCap(), boosted)
  })

  const getStalledProgressMs = (): number => Date.now() - lastRealProgressAt.value

  const progressPercent = displayProgressPercent

  const currentStepIndex = computed((): number => {
    const parts = displayParts.value
    if (parts.length === 0) { return 0 }
    const doneCount = parts.filter(part => stepState(part) === 'done').length
    if (doneCount >= parts.length) { return parts.length }
    return doneCount + 1
  })

  const currentStepLabel = computed((): string => {
    const syncingPart = STORE_SYNC_PARTS.find(part => status.value?.parts[part].isSyncing)
    if (syncingPart) { return syncingPart }
    const parts = displayParts.value
    const nextPending = parts.find(part => status.value?.parts[part].needsSync)
    return nextPending ?? parts[0] ?? 'merchant'
  })

  const stepState = (part: StoreSyncPart): StoreSyncStepState => {
    if (!displayParts.value.includes(part)) { return 'pending' }
    const partStatus = status.value?.parts[part]
    if (!partStatus) { return 'pending' }
    if (partStatus.errorMessage) { return 'failed' }
    if (partStatus.isSyncing) { return 'active' }
    if (!partStatus.needsSync) { return 'done' }
    return 'pending'
  }

  const reset = (): void => {
    status.value = null
    currentAppId.value = null
    statusFetchedAt.value = null
    fetchStatusPromise = null
    fetchStatusAppId = null
    modalOpen.value = false
    modalPhase.value = 'review'
    selectedParts.value = [...STORE_SYNC_PARTS]
    activeParts.value = []
    optimisticPendingParts.value = new Set()
    resetFakeProgress()
    syncStartedAt.value = null
    dismissBanner()
  }

  return {
    status,
    syncing,
    needsSync,
    isMenuPending,
    pendingSyncParts,
    isMenuOnlyPending,
    bannerPhase,
    modalOpen,
    modalPhase,
    selectedParts,
    activeParts,
    displayParts,
    actualProgressPercent,
    displayProgressPercent,
    progressPercent,
    currentStepIndex,
    currentStepLabel,
    fetchStatus,
    refreshIfSyncing,
    openReviewModal,
    openProgressModal,
    openErrorModal,
    closeModal,
    dismissBanner,
    syncSelected,
    syncAll,
    triggerSync,
    retrySync,
    markPartStale,
    toggleSelectedPart,
    selectAllParts,
    clearSelectedParts,
    setSelectAll,
    stepState,
    resetFakeProgress,
    beginFakeProgress,
    tickFakeProgress,
    getStalledProgressMs,
    getSyncElapsedMs,
    handleSyncTimeout,
    reset
  }
})

export default useStoreSyncStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useStoreSyncStore, (import.meta as any).hot))
}
